import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { queryGetUserAssociatedCorpList } from "@type/api/corporation";
import { useAuthStore } from "@type/store";
import { TCorporation } from "@type/store/CorporationStore";
import { useQueryWithCallbacks } from "lib/query/useQueryWithCallbacks";

interface UseGetUserAssociatedCorpListProps {
  perPage?: number;
  page?: number;
}

export const useGetUserAssociatedCorpList = ({
  perPage,
  page,
}: UseGetUserAssociatedCorpListProps) => {
  const [loading, setLoading] = useState(true);
  const [corpList, setCorpList] = useState<TCorporation[]>([]);
  const [mapCorpList, setMapCorpList] = useState<Record<string, TCorporation>>(
    {}
  );

  const [corpListParams, setCorpListParams] = useState({
    idMember: "",
    key: "",
    page: 0,
    pageSize: 100,
  });

  const procesedPage = useRef<number[]>([]);
  const user = useAuthStore((state) => state.user);

  const queryGetUserAssociatedCorpListKey = useMemo(() => {
    const { ...restParams } = corpListParams;
    return queryGetUserAssociatedCorpList._key(restParams);
  }, [corpListParams]);

  useQueryWithCallbacks({
    queryKey: queryGetUserAssociatedCorpListKey,
    enabled:
      !!corpListParams?.page &&
      !!corpListParams?.idMember &&
      !!corpListParams?.page &&
      !!corpListParams?.pageSize,
    queryFn: () => queryGetUserAssociatedCorpList(corpListParams),
    onSuccess: (res) => {
      if (!procesedPage.current.includes(res.page)) {
        const newMapData: Record<string, TCorporation> = {};
        const corpListWithSlug: TCorporation[] = [];
        for (const corp of res.data) {
          const corpWithSlug = {
            ...corp,
            slug: corp.idUnique,
          };

          corpListWithSlug.push(corpWithSlug);
          newMapData[corp.idUnique] = corpWithSlug;
        }

        if (res.page === 1) {
          setCorpList(corpListWithSlug);
          setMapCorpList(newMapData);
        } else {
          const corpListArray = Object.values(corpListWithSlug);
          setCorpListParams((prev) => ({
            ...prev,
            ...corpListArray,
          }));

          setMapCorpList((prev) => ({
            ...prev,
            ...newMapData,
          }));
        }

        if (res.page * res.pageSize < res.total && !page) {
          setCorpListParams((prev) => ({
            ...prev,
            key: uuidv4(),
            page: res.page + 1,
          }));
        } else {
          setLoading(false);
        }

        procesedPage.current = [...procesedPage.current, res.page];
      }
    },
  });

  useEffect(() => {
    if (user.idUnique && !corpListParams.idMember) {
      setCorpListParams((prev) => ({
        ...prev,
        key: uuidv4(),
        page: page || 1,
        pageSize: perPage || 100,
        idMember: user.idUnique,
      }));
    }
  }, [user.idUnique, corpListParams?.idMember, perPage, page]);

  return {
    loading,
    corpList,
    mapCorpList,
  };
};
